import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';

// Define an interface extending Navigator for the standalone property
interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  private readonly PWA_LAST_ROUTE = 'pwa_last_route';
  private readonly HOME_ROUTE = '/pages/dashboards/employee';
  private readonly POS_ROUTE = '/pages/point-of-sale/orders-creation';
  private readonly LOGIN_ROUTE = '/auth/login';

  constructor(
    @Optional() private swUpdate: SwUpdate,
    private router: Router,
    private authService: AuthService,
    private _translate: TranslateService
  ) {
    // Initialize with a slight delay to ensure app has loaded
    setTimeout(() => {
      this.initPwaSessionHandling();
    }, 1000);

    // Register visibility change event safely
    this.registerVisibilityChangeHandler();
  }

  /**
   * Initialize PWA session handling on startup
   */
  private initPwaSessionHandling(): void {
    try {
      // Check for app launch in standalone mode (installed PWA)
      if (this.isPwaMode()) {
        console.log('PWA Mode detected, handling cold launch');
        // Only on a genuine cold launch do we restore the last route / pick a landing page
        this.handleColdLaunch();
      } else {
        console.log('Regular browser mode detected');
      }
    } catch (error) {
      console.error('Error initializing PWA session handling:', error);
    }
  }

  /**
   * Register visibility change handler to detect when app is brought to foreground
   */
  private registerVisibilityChangeHandler(): void {
    try {
      fromEvent(document, 'visibilitychange').subscribe(() => {
        // When app becomes visible again after being backgrounded/minimized,
        // the app's in-memory state and current route are still intact.
        // We must NOT navigate away, otherwise the user loses the page they
        // were on and gets bounced to the dashboard.
        if (document.visibilityState === 'visible' && this.isPwaMode()) {
          console.log('App became visible in PWA mode - keeping current route');
        } else if (document.visibilityState === 'hidden' && this.isPwaMode()) {
          // App going to background - save current route for cold-start restoration
          this.saveCurrentRoute();
        }
      });
    } catch (error) {
      console.error('Error registering visibility change handler:', error);
    }
  }

  /**
   * Check if the app is running in PWA mode (installed or standalone)
   */
  public isPwaMode(): boolean {
    try {
      const standaloneMode = window.matchMedia('(display-mode: standalone)').matches;
      const fullscreenMode = window.matchMedia('(display-mode: fullscreen)').matches;
      const navigatorStandalone = (window.navigator as NavigatorWithStandalone).standalone === true;

      console.log('PWA detection:', {
        standaloneMode,
        fullscreenMode,
        navigatorStandalone
      });

      return standaloneMode || fullscreenMode || navigatorStandalone;
    } catch (error) {
      console.error('Error checking PWA mode:', error);
      return false;
    }
  }

  /**
   * Save the current route for potential restoration
   */
  private saveCurrentRoute(): void {
    try {
      // Store current path if user is logged in
      if (this.isLoggedIn()) {
        localStorage.setItem(this.PWA_LAST_ROUTE, this.router.url);
      }
    } catch (error) {
      console.error('Error saving current route:', error);
    }
  }

  /**
   * Handle a genuine cold launch (app started from scratch via the manifest
   * start_url). Restore the last route the user was on if available, otherwise
   * fall back to a sensible landing page.
   * When offline with an active POS session, go directly to POS.
   */
  private handleColdLaunch(): void {
    try {
      if (this.isLoggedIn()) {
        // If offline and has active POS session, go straight to POS
        if (!navigator.onLine && this.hasActivePosSession()) {
          console.log('Offline with active POS session, navigating to POS');
          this.router.navigate([this.POS_ROUTE]);
          return;
        }

        const lastRoute = this.getSavedRoute();
        if (lastRoute) {
          console.log('Restoring last route on cold launch:', lastRoute);
          this.router.navigateByUrl(lastRoute);
        } else {
          console.log('No saved route, navigating to home page');
          this.router.navigate([this.HOME_ROUTE]);
        }
      } else {
        console.log('User not logged in, navigating to login page');
        this.router.navigate([this.LOGIN_ROUTE]);
      }
    } catch (error) {
      console.error('Error handling cold launch:', error);
    }
  }

  /**
   * Retrieve the saved route, ignoring auth routes so we never restore into login.
   */
  private getSavedRoute(): string | null {
    try {
      const saved = localStorage.getItem(this.PWA_LAST_ROUTE);
      if (!saved || saved.startsWith('/auth')) {
        return null;
      }
      return saved;
    } catch {
      return null;
    }
  }

  /**
   * Check if there's an active POS session in localStorage
   */
  private hasActivePosSession(): boolean {
    try {
      const sessionData = localStorage.getItem('SessionData');
      return !!sessionData;
    } catch {
      return false;
    }
  }

  /**
   * Check if user is logged in by verifying access token
   */
  private isLoggedIn(): boolean {
    try {
      const accessToken = localStorage.getItem('ACCESS_TOKEN');
      return !!accessToken;
    } catch (error) {
      console.error('Error checking login status:', error);
      return false;
    }
  }

  /**
   * Check for available updates
   */
  public checkForUpdates(): void {
    try {
      const updates = this.swUpdate;
      if (updates?.isEnabled) {
        // Updated to use versionUpdates instead of available
        updates.versionUpdates
          .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
          .subscribe(() => {
            try {
              // Prompt user to update
              if (confirm(this._translate.instant('PWA_SERVICE.UPDATE_AVAILABLE'))) {
                window.location.reload();
              }
            } catch (error) {
              console.error('Error handling update prompt:', error);
            }
          });
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  }
}